import { Button, Select } from '@trackman/web-shared-components';
import { DexterityValue, ProfileData, SignUpLists, dexterityOptionToValue } from 'types/profile';

import { ReactComponent as TourPlayerIcon } from 'images/icons/TourPlayer.svg';
import i18next, { t } from 'i18next';
import { useGetProfile, useGetSignUpLists, useSaveProfile } from 'queryHooks/profile';
import { useTranslation } from 'react-i18next';
import { usePlayerInformationFormStore } from 'stores/Profile/ProfileStore';
import { useEffect } from 'react';

const handicapOptions = [...Array(42).keys()]
  .map((i) => i - 5)
  .map((integer) => {
    return {
      label: integer < 0 ? `+${integer * -1}` : integer === 36 ? '36+' : integer.toString(),
      value: integer.toString(),
    };
  });

const unitSystemOptions = [
  { label: i18next.t('Imperial', { ns: 'userCulture' }), value: 'en-US' },
  { label: i18next.t('Japanese Units', { ns: 'userCulture' }), value: 'ja-JP' },
  { label: i18next.t('Korean Units', { ns: 'userCulture' }), value: 'ko-KR' },
  { label: i18next.t('Metric', { ns: 'userCulture' }), value: 'en-GB' },
];

const categories = [
  { label: i18next.t('TeachingPro', { ns: 'editProfile' }), value: 'Teaching Pro' },
  { label: i18next.t('TourPro', { ns: 'editProfile' }), value: 'Tour Pro' },
  { label: i18next.t('ClubfitterRetail', { ns: 'editProfile' }), value: 'Clubfitter / Retail' },
  { label: i18next.t('EQM', { ns: 'editProfile' }), value: 'EQM' },
  { label: i18next.t('Amateur', { ns: 'editProfile' }), value: 'Amateur' },
  { label: i18next.t('Student', { ns: 'editProfile' }), value: 'Student' },
  { label: i18next.t('Other', { ns: 'editProfile' }), value: 'Other' },
];

export const PlayerSettingsTab = () => {
  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();
  const { data: signUpLists, isPending: isSignUpListsPending } = useGetSignUpLists();

  return (
    <>
      {!isProfileDataPending && !isSignUpListsPending && profileData && signUpLists && (
        <PlayerSettingsForm profileData={profileData} signUpLists={signUpLists} />
      )}
    </>
  );
};

const PlayerSettingsForm = ({ profileData, signUpLists }: { profileData: ProfileData; signUpLists: SignUpLists }) => {
  const { t } = useTranslation();

  const dexterity = usePlayerInformationFormStore((s) => s.dexterity);
  const handicap = usePlayerInformationFormStore((s) => s.handicap);
  const category = usePlayerInformationFormStore((s) => s.category);
  const culture = usePlayerInformationFormStore((s) => s.culture);
  const nationality = usePlayerInformationFormStore((s) => s.nationality);
  const setDexterity = usePlayerInformationFormStore((s) => s.setDexterity);
  const setHandicap = usePlayerInformationFormStore((s) => s.setHandicap);
  const setCategory = usePlayerInformationFormStore((s) => s.setCategory);
  const setCulture = usePlayerInformationFormStore((s) => s.setCulture);
  const setNationality = usePlayerInformationFormStore((s) => s.setNationality);
  const initFromProfileData = usePlayerInformationFormStore((s) => s.initFromProfileData);

  useEffect(() => {
    initFromProfileData(profileData);
  }, [profileData]);

  const saveProfilePayload: ProfileData = {
    ...profileData!,
    dexterity,
    handicap,
    category,
    culture,
    nationality,
  };

  const saveProfile = useSaveProfile(saveProfilePayload);

  return (
    <div className='profile-section'>
      <div className='profile-inputs-column'>
        <div className='profile-inputs-column-title'>
          <TourPlayerIcon />
          {t('PlayerInformation', { ns: 'editProfile' })}
        </div>
        <Select
          label={t('Dexterity', { ns: 'editProfile' })}
          value={dexterity}
          hint=' '
          onChange={(event) => {
            setDexterity(event.target.value as DexterityValue);
          }}
          selectOptions={Object.entries(dexterityOptionToValue).map((option) => {
            return {
              label: option[0],
              value: option[1],
            };
          })}
        />
        <Select
          label={t('Handicap', { ns: 'editProfile' })}
          value={handicap.toString()}
          hint=' '
          onChange={(event) => {
            setHandicap(Number.parseFloat(event.target.value as string));
          }}
          selectOptions={handicapOptions}
        />
        <Select
          label={t('PlayerCategory', { ns: 'editProfile' })}
          value={category}
          hint=' '
          onChange={(event) => {
            setCategory(event.target.value as string);
          }}
          selectOptions={categories}
        />
      </div>
      <div className='profile-inputs-column no-subtitle'>
        <Select
          label={t('UnitSystem', { ns: 'editProfile' })}
          value={culture}
          hint=' '
          onChange={(event) => {
            setCulture(event.target.value as string);
          }}
          selectOptions={unitSystemOptions}
        />
        <Select
          label={t('Nationality', { ns: 'editProfile' })}
          value={nationality}
          hint=' '
          onChange={(event) => {
            setNationality(event.target.value as string);
          }}
          selectOptions={signUpLists.Countries.map((country) => {
            return {
              label: country.name === ' ' ? 'Please select a country' : country.name,
              value: country.name,
            };
          })}
        />
        <div className='button-wrapper'>
          <Button
            label={t('SaveChanges', { ns: 'editProfile' })}
            onClick={() => saveProfile.mutate()}
            intent='primary'
            isLoading={saveProfile.isPending}
            disabled={saveProfile.isPending}
            className='styled-button primary'
          />
        </div>
      </div>
    </div>
  );
};

export default PlayerSettingsTab;
